import { Injectable, inject } from '@angular/core';
import { AppConstants } from '@core/constants';
import { ChartConstants } from '@modules/apc/apc/data/chart-modules';
import { ReportConfig } from '@modules/report-generator/interfaces';
import { CustomRendererService } from '@shared/services/custom-renderer.service';
import * as _ from 'lodash-es';

declare let Bokeh_v0123: any;

const Defaults = {
  PlotWidth: AppConstants.METABOLIC_PROFILE.BODY_COMPOSITION.PIE_CHART.WIDTH,
  PlotHeight: AppConstants.METABOLIC_PROFILE.BODY_COMPOSITION.PIE_CHART.HEIGHT,
  BackgroundColor: '#FFF',
  BorderColor: '#FFF',
  Legend: {
    BackgroundColor: '#FFF',
    BorderWidth: 0,
    BackgroundFillAlpha: 0.75,
    TextColor: '#444444',
  },
};

@Injectable({
  providedIn: 'root',
})
export class BodyCompositionPieChartService {
  private readonly plottingAPI = Bokeh_v0123.Plotting;
  private readonly customRendererService = inject(CustomRendererService);

  destroyChart(elementId: string) {
    this.customRendererService.emptyAndCleanupBokeh(elementId || ChartConstants.BODY_COMPOSITION.ELEMENT_ID);
  }

  renderBodyComposition(
    metabolicPlot0: Record<string, any>,
    elementId?: string,
    scaleText = 1,
    config?: ReportConfig
  ): void {
    const hasPlots = Boolean(
      metabolicPlot0.plots && Array.isArray(metabolicPlot0.plots) && metabolicPlot0.plots.length > 0
    );
    if (!hasPlots) {
      return;
    }

    const bodyCompositionPlot = metabolicPlot0.plots[0];
    this.customRendererService.emptyAndCleanupBokeh(elementId || ChartConstants.BODY_COMPOSITION.ELEMENT_ID);
    this.generateBodyComposition(
      bodyCompositionPlot,
      elementId || ChartConstants.BODY_COMPOSITION.ELEMENT_ID,
      scaleText,
      config
    );
  }

  private generateBodyComposition(
    bodyCompositionPlot: Record<string, any>,
    elementId: string,
    scaleText = 1,
    config?: ReportConfig
  ): void {
    const primary = config?.template?.theme?.colors?.primary;
    const secondary = config?.template?.theme?.colors?.secondary;

    const pieChart = this.drawPieChart(bodyCompositionPlot, scaleText, primary, secondary);
    const combinedChart = this.plottingAPI.gridplot(
      [
        [pieChart], // Hide barChart
      ],
      {
        toolbar_location: null,
        sizing_mode: 'scale_width',
      }
    );
    this.plottingAPI.show(combinedChart, elementId);
  }

  public drawPieChart(plot: any, scaleText = 1, primaryColor?: string, secondaryColor?: string) {
    let pieChart: any;
    if (plot && plot.wedges && plot.wedges.length > 0) {
      // create range for chart
      let xdr = null;
      let ydr = null;

      if (plot.x_range && plot.x_range.length > 1) {
        xdr = Bokeh_v0123.Range1d(plot.x_range[0], plot.x_range[1]);
      }
      if (plot.y_range && plot.y_range.length > 1) {
        ydr = Bokeh_v0123.Range1d(plot.y_range[0], plot.y_range[1]);
      }

      //Create a raw plot chart with default width,height and range
      pieChart = this.plottingAPI.figure({
        plot_width: Defaults.PlotWidth,
        plot_height: Defaults.PlotHeight,
        x_range: xdr,
        y_range: ydr,
        tools: false,
        background_fill_color: Defaults.BackgroundColor,
        border_fill_color: Defaults.BorderColor,
        outline_line_color: null,
        sizing_mode: 'scale_width',
      });

      if (pieChart) {
        //Convert to a BokehJS API param object
        let wedgeParams;
        _.each(plot.wedges, (wedge): void => {
          let wedgeColor = wedge.color;
          if ((wedge.legend as string).includes('fat free')) {
            wedgeColor = primaryColor ?? wedgeColor;
          } else {
            wedgeColor = secondaryColor ?? wedgeColor;
          }
          wedgeParams = {
            x: wedge.x,
            y: wedge.y,
            radius: wedge.radius,
            start_angle: wedge.start_angle,
            end_angle: wedge.end_angle,
            color: wedgeColor,
            legend: wedge.legend,
          };

          pieChart.wedge(wedgeParams);
        });

        //Reset legend position and orientation for first chart
        pieChart._legend.orientation = 'horizontal';
        pieChart._legend.location = 'bottom_left';
        pieChart._legend.background_fill_color = Defaults.Legend.BackgroundColor;
        pieChart._legend.border_line_width = Defaults.Legend.BorderWidth;
        pieChart._legend.background_fill_alpha = Defaults.Legend.BackgroundFillAlpha;
        pieChart._legend.label_text_color = Defaults.Legend.TextColor;

        const fontSize = 10 * scaleText;
        const padding = 4 * scaleText;
        pieChart._legend.label_text_font_size = `${fontSize}px`;
        pieChart._legend.padding = padding;

        //Hide both chart's axis
        pieChart.xaxis.visible = false;
        pieChart.yaxis.visible = false;
        //Reset min border value
        // pieChart.min_border = 20;

        //remove grid lines in chart
        pieChart.xgrid.grid_line_color = null;
        pieChart.ygrid.grid_line_color = null;
      }
    }

    return pieChart;
  }
}
